import { z } from "zod";

/**
 * Individual scope key for agent integration connections.
 * Determines which entity dimension(s) an integration is bound to.
 */
export const integrationScopeKeySchema = z
  .enum(["user", "project", "workspace"])
  .describe("IntegrationScopeKey");

export type IntegrationScopeKey = z.infer<typeof integrationScopeKeySchema>;

/**
 * Integration scope determines which entities the integration is connected to.
 * Each key set to true means that dimension is part of the scope (at least one required).
 *
 * Examples:
 * - { user: true } → One connection per user (global to that user across all projects)
 * - { project: true } → One connection per project (shared by all users in the project)
 * - { workspace: true } → One connection per workspace (shared by all users/projects)
 * - { user: true, project: true } → One connection per user per project
 */
export const integrationScopeSchema = z
  .object({
    user: z.literal(true).optional(),
    project: z.literal(true).optional(),
    workspace: z.literal(true).optional(),
  })
  .refine(
    (scope) => {
      return (
        scope.user === true ||
        scope.project === true ||
        scope.workspace === true
      );
    },
    { message: "At least one scope key required" },
  )
  .describe("IntegrationScope");

export type IntegrationScope = z.infer<typeof integrationScopeSchema>;
